import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Alert,
  Button,
  Fieldset,
  Group,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import {
  COMMON_TIMEZONES,
  channelOf,
  instantToWallClock,
  normaliseSeries,
  risChannels,
  wallClockToInstant,
} from '@racedash/shared'
import type { EventSource, RisFeed, RisSource } from '@racedash/shared'
import { updateSeries, type Event, type EventInput, type Series } from '../data/catalog'
import { useTracks } from '../data/tracks'

/** Everything the form owns, in the shape it is compared in. */
interface Draft {
  name: string
  trackId: string
  timezone: string
  starts: string | null
  ends: string | null
}

/**
 * The reusable half of event entry.
 *
 * Name, circuit, timezone and dates are the same whoever times the event. What
 * differs is *addressing*, which is entirely vendor-specific — so that part is
 * passed in rather than branched on here.
 *
 * On an existing event the form tracks what has actually changed. Save is dead
 * until something has, and Revert puts it back. That makes the unsaved state
 * visible in the page rather than something you have to remember, which is what
 * a navigation guard would otherwise be for — and a guard that interrupts you
 * is a worse answer than a button that tells you.
 *
 * `vendorOwned` marks the event as one we discovered rather than one we entered.
 * The fields a scrape re-derives are then shown rather than offered for editing,
 * because a form that accepts an edit and has it silently reverted overnight is
 * worse than one that does not offer it.
 */
export default function EventForm({
  event,
  source,
  sourceFields,
  /** Whether the vendor block has unsaved edits. Only the caller knows. */
  sourceDirty = false,
  /** Set when a scrape owns name, circuit and dates — e.g. 'TSL'. */
  vendorOwned,
  onSubmit,
  onRevert,
  onCancel,
  onDirtyChange,
  submitLabel = 'Save',
}: {
  event?: Event
  source: EventSource
  sourceFields: ReactNode
  sourceDirty?: boolean
  vendorOwned?: string
  onSubmit: (input: Omit<EventInput, 'source'>) => Promise<void>
  /** Called alongside the form's own reset, so the caller can drop its
   *  vendor-block edits at the same time. */
  onRevert?: () => void
  onCancel?: () => void
  /** Reported upward so the route can guard a navigation away from edits the
   *  form knows about but the page does not. */
  onDirtyChange?: (dirty: boolean) => void
  submitLabel?: string
}) {
  const initial = useMemo<Draft>(
    () => ({
      name: event?.name ?? '',
      trackId: event?.trackId ?? '',
      timezone: event?.timezone ?? 'Europe/London',
      starts: initialDate(event, 'startsAt', event?.timezone),
      ends: initialDate(event, 'endsAt', event?.timezone),
    }),
    [event],
  )

  const { tracks } = useTracks()
  const [draft, setDraft] = useState<Draft>(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const editing = Boolean(event)
  const changed =
    sourceDirty || (Object.keys(initial) as (keyof Draft)[]).some((k) => draft[k] !== initial[k])
  const complete = Boolean(draft.name.trim() && draft.trackId.trim() && draft.starts && draft.ends)

  useEffect(() => {
    onDirtyChange?.(editing && changed)
    // Unmounting means the form is gone and so are its edits, so the guard must
    // not be left armed against a form that no longer exists.
    return () => onDirtyChange?.(false)
  }, [editing, changed, onDirtyChange])

  function revert() {
    setDraft(initial)
    setError(null)
    onRevert?.()
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    if (!complete) {
      setError('Name, circuit and both dates are required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit({
        name: draft.name.trim(),
        trackId: draft.trackId.trim(),
        timezone: draft.timezone,
        // Dates in, instants out. An event runs from the start of its first
        // day to the end of its last, in its own timezone — so the conversion
        // happens here and nothing downstream handles a zone.
        startsAt: wallClockToInstant(`${draft.starts}T00:00`, draft.timezone),
        endsAt: wallClockToInstant(`${draft.ends}T23:59`, draft.timezone),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <Stack gap="md">
        {error && <Alert color="red">{error}</Alert>}

        {vendorOwned && (
          <Alert color="gray" variant="light">
            Name, circuit and dates come from the {vendorOwned} diary and are refreshed by the
            nightly scrape, so they are shown here rather than edited — an edit would be silently
            replaced the next time it runs. The timezone is ours and is kept.
          </Alert>
        )}

        {vendorOwned ? (
          <>
            <Shown label="Event name" value={draft.name} />
            <Shown
              label="Circuit"
              value={tracks?.find((t) => t.id === draft.trackId)?.name ?? draft.trackId}
            />
          </>
        ) : (
          <>
            <TextInput
              label="Event name"
              value={draft.name}
              onChange={(e) => set('name', e.currentTarget.value)}
              placeholder="Portimão FunCup weekend"
              required
            />
            <Select
              label="Circuit"
              data={(tracks ?? []).map((t) => ({ value: t.id, label: t.name }))}
              value={draft.trackId || null}
              onChange={(v) => {
                set('trackId', v ?? '')
                // A circuit knows its own timezone, so choosing one answers the
                // next question too — but only for a new event, since changing
                // the circuit on an existing one should not quietly move its
                // sessions to another clock.
                const track = tracks?.find((t) => t.id === v)
                if (track && !event) set('timezone', track.timezone)
              }}
              searchable
              placeholder="Choose a circuit…"
              description="Picked rather than typed: the identifier is what a live stream is matched against, and what partitions the raw capture log. Add a missing one under Circuits."
              required
            />
          </>
        )}

        <Select
          label="Timezone"
          data={COMMON_TIMEZONES}
          value={draft.timezone}
          onChange={(v) => set('timezone', v ?? 'Europe/London')}
          searchable
          allowDeselect={false}
          description="Timetables are published in local wall-clock time, and a circuit does not fix the offset across a daylight-saving change — so this is asked for rather than guessed."
        />

        {vendorOwned ? (
          <Group grow align="flex-start">
            <Shown label="First day" value={formatDay(draft.starts)} />
            <Shown label="Last day" value={formatDay(draft.ends)} />
          </Group>
        ) : (
          <>
            <Group grow align="flex-start">
              <DatePickerInput
                label="First day"
                value={draft.starts}
                onChange={(v) => {
                  // Most events are a single day, and a multi-day one still
                  // starts from its first — so seed the end rather than making
                  // it be typed twice. Only when it has not been set, so an
                  // edit is never silently overwritten.
                  setDraft((d) => ({ ...d, starts: v, ends: d.ends ?? v }))
                }}
                required
              />
              <DatePickerInput
                label="Last day"
                value={draft.ends}
                onChange={(v) => set('ends', v)}
                minDate={draft.starts ?? undefined}
                required
              />
            </Group>
            <Text c="dimmed" size="xs" mt={-8}>
              Dates, not times — a meeting runs from the start of its first day to the end of its
              last, in {draft.timezone}. Session times are set per session.
            </Text>
          </>
        )}

        <Fieldset legend={`Timing source — ${source.vendor.toUpperCase()}`}>{sourceFields}</Fieldset>

        <Group>
          <Button type="submit" loading={busy} disabled={editing && !changed}>
            {submitLabel}
          </Button>
          {editing && (
            <Button
              variant="subtle"
              color="gray"
              type="button"
              onClick={revert}
              disabled={!changed}
            >
              Revert
            </Button>
          )}
          {onCancel && (
            <Button variant="subtle" color="gray" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {editing && changed && (
            <Text c="dimmed" size="xs">
              Unsaved changes.
            </Text>
          )}
        </Group>
      </Stack>
    </form>
  )
}

/** A value that is displayed rather than edited, laid out like the inputs
 *  beside it so the form still reads as one thing. */
function Shown({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="sm" fw={500} mb={2}>
        {label}
      </Text>
      <Text size="sm" c="dimmed">
        {value || '—'}
      </Text>
    </div>
  )
}

function formatDay(iso: string | null): string {
  const parts = iso?.split('-').map(Number)
  if (!parts || parts.length !== 3 || parts.some(Number.isNaN)) return ''
  const [y, m, d] = parts as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

// Renders a stored instant back as the calendar date it represents in the
// event's own timezone. Slicing the wall-clock form rather than the ISO string
// matters: an event ending 23:59 local can be the next day in UTC, and a naive
// toISOString would show the wrong last day.
function initialDate(
  event: Event | undefined,
  key: 'startsAt' | 'endsAt',
  timezone: string | undefined,
): string | null {
  const ts = event?.[key]
  if (!ts?.toDate || !timezone) return null
  return instantToWallClock(ts.toDate(), timezone).slice(0, 10)
}

/**
 * RIS addressing.
 *
 * The two feeds are independent — different hosts, different addressing, and
 * observed serving different events at the same moment — so which one carries a
 * given race has to be recorded, not assumed.
 */
export function RisSourceFields({
  source,
  onChange,
  series,
  eventSeriesIds,
  captured,
}: {
  source: RisSource
  onChange: (source: RisSource) => void
  /** Every series in the catalogue, to name rows and to add one. */
  series?: Series[] | null
  /** The series this event's timetable names, offered without being added. */
  eventSeriesIds?: string[]
  /** What was captured for the event, for the names each channel used. */
  captured?: { id: string; seriesNameRaw?: string }[] | null
}) {
  return (
    <Stack gap="md">
      <Radio.Group
        label="Feed"
        value={source.feed}
        onChange={(v) => onChange({ ...source, feed: v as RisFeed })}
        description="The JSON and XML feeds are separate services on different hosts, and have been seen carrying different events simultaneously. If unsure, capture both."
      >
        <Group mt="xs" gap="lg">
          <Radio value="json" label="JSON" />
          <Radio value="xml" label="XML" />
        </Group>
      </Radio.Group>

      {source.feed === 'json' ? (
        <>
          <Alert color="gray" variant="light">
            {/* There used to be a uuid field here, filled in by hand from the
                event's own website close to the event. It turned out not to be
                an address: the JSON feed is seventeen fixed channels and the
                "uuid" is an index into them, published on a page that is
                routinely stale and occasionally wrong. */}
            Capture sweeps the Fun Cup channels and follows whichever is carrying the event,
            because RIS publishes no way to ask which that will be. Nothing below changes
            that — everything is captured either way.
          </Alert>
          <SeriesChannels
            source={source}
            onChange={onChange}
            series={series ?? []}
            eventSeriesIds={eventSeriesIds ?? []}
            captured={captured ?? []}
          />
        </>
      ) : (
        <TextInput
          label="Path segment"
          value={source.path ?? ''}
          onChange={(e) =>
            onChange({ ...source, path: e.currentTarget.value.trim().replace(/\//g, '') })
          }
          placeholder="funfr"
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          description="From results.ris-timing.be/{path}/live/. This names a timing installation rather than a series — the same path has been seen carrying different championships."
        />
      )}
    </Stack>
  )
}

/**
 * Which channel each series' teams read their timing from.
 *
 * A combined round carries the French cars on /funfr, the Belgian cars on
 * /funbe and the whole field on the root. A team racing for the French
 * championship wants its place among French cars; a team doing a few rounds
 * wants the overall screen. That is decided here, per series, so nobody
 * watching the race is ever asked which channel to use.
 *
 * Rows are the series the timetable names plus any already mapped; a series
 * a team entered under that is not on the timetable can be added.
 */
function SeriesChannels({
  source,
  onChange,
  series,
  eventSeriesIds,
  captured,
}: {
  source: RisSource
  onChange: (source: RisSource) => void
  series: Series[]
  eventSeriesIds: string[]
  captured: { id: string; seriesNameRaw?: string }[]
}) {
  const map = source.seriesChannels ?? {}
  const [extra, setExtra] = useState<string[]>([])
  const rows = [...new Set([...eventSeriesIds, ...Object.keys(map), ...extra])]
  const nameOf = (id: string) => series.find((x) => x.id === id)?.name ?? id
  const addable = series.filter((x) => !rows.includes(x.id)).map((x) => ({ value: x.id, label: x.name }))
  const channelOptions = risChannels.map((c) => ({ value: c.id, label: `${c.path} — ${c.note}` }))

  const set = (seriesId: string, channel: string | null) => {
    const next = { ...map }
    if (channel) next[seriesId] = channel
    else delete next[seriesId]
    onChange({ ...source, seriesChannels: Object.keys(next).length > 0 ? next : undefined })
  }

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>
        Channel per series
      </Text>
      <Text size="xs" c="dimmed">
        Which channel each series' teams see. At a combined round: the French championship on
        /funfr, the Belgian on /funbe, and the root for a series that wants the overall screen. A
        series with no channel is not shown to anybody. A session is the series' only where the
        channel names it with one of the series' names.
      </Text>
      {rows.length > 0 && (
        <Table verticalSpacing={4} horizontalSpacing="sm">
          <Table.Tbody>
            {rows.map((id) => (
              <Table.Tr key={id}>
                <Table.Td w="40%">
                  <Text size="sm">{nameOf(id)}</Text>
                </Table.Td>
                <Table.Td>
                  <NamesOnChannel
                    series={series.find((x) => x.id === id) ?? null}
                    channel={map[id] ?? null}
                    captured={captured}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    aria-label={`Channel for ${nameOf(id)}`}
                    size="xs"
                    data={channelOptions}
                    value={map[id] ?? null}
                    onChange={(v) => set(id, v)}
                    placeholder="Not shown to teams"
                    clearable
                    searchable
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {addable.length > 0 && (
        <Select
          size="xs"
          w={260}
          data={addable}
          value={null}
          onChange={(v) => v && setExtra((xs) => [...xs, v])}
          placeholder="Add a series…"
          searchable
        />
      )}
    </Stack>
  )
}

/**
 * Whether the chosen channel's sessions are this series', by name.
 *
 * A session is a series' only where the channel names it with one of the
 * series' names, and channels spell championships their own way: /funbe says
 * "Fun Cup BE" for what the root calls "Fun Cup Belgium". So each row says how
 * many captured sessions match, and where none do, offers the names the
 * channel did use, one click from being added to the series.
 */
function NamesOnChannel({
  series,
  channel,
  captured,
}: {
  series: Series | null
  channel: string | null
  captured: { id: string; seriesNameRaw?: string }[]
}) {
  // What was added from this row, kept across the re-render the addition
  // itself causes, so the row can say what just changed and where.
  const [added, setAdded] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  if (!series || !channel) return null
  const known = new Set((series.vendorNames?.ris ?? []).map(normaliseSeries))
  const onChannel = captured.filter((c) => channelOf(c.id) === channel && c.seriesNameRaw)

  const confirmation = added && (
    <Text size="xs" c="teal">
      Added “{added}” to the names of {series.name}. This applies at every event, not only this one.
    </Text>
  )

  if (onChannel.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Nothing captured on {channel} yet
      </Text>
    )
  }
  const matching = onChannel.filter((c) => known.has(normaliseSeries(c.seriesNameRaw as string))).length
  if (matching > 0) {
    return (
      <Stack gap={2}>
        <Text size="xs" c="dimmed">
          {matching} session{matching === 1 ? '' : 's'} on {channel} shown to {series.name} teams
        </Text>
        {confirmation}
      </Stack>
    )
  }

  const names = [...new Set(onChannel.map((c) => c.seriesNameRaw as string))]
  const add = async (name: string) => {
    setBusy(name)
    setFailed(null)
    try {
      await updateSeries(series.id, {
        name: series.name,
        vendorNames: { ...series.vendorNames, ris: [...(series.vendorNames?.ris ?? []), name] },
      })
      setAdded(name)
    } catch (e) {
      setFailed((e as Error).message)
    } finally {
      setBusy(null)
    }
  }
  return (
    <Stack gap={4}>
      <Text size="xs" c="orange">
        Teams in {series.name} will see nothing from {channel}: none of its sessions is named as
        {' '}{series.name}. The timing system on {channel} uses these names instead. Add the one
        that means {series.name} to the series, and it is recognised at every event.
      </Text>
      <Group gap={4}>
        {names.map((n) => (
          <Button
            key={n}
            size="compact-xs"
            variant="light"
            loading={busy === n}
            disabled={busy !== null}
            onClick={() => void add(n)}
          >
            Add “{n}” to {series.name}
          </Button>
        ))}
      </Group>
      {failed && (
        <Text size="xs" c="red">
          Could not update the series: {failed}
        </Text>
      )}
    </Stack>
  )
}
